import { isPlatformBrowser, Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { PATH_NAMES, SEO } from '../../../shared/constants';
import { IHomeBanner, IHomeBannerResponse } from '../../../shared/models';
import { SeoService } from '../../../services/seo/seo.service';
import { TabIndex } from '../constants';
import { makeStateKey, TransferState } from '@angular/platform-browser';

const PROMO_BANNERS_KEY = makeStateKey<IHomeBanner[]>('home-banners-data');

@Injectable({
  providedIn: 'root',
})
export class HomeService {
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private readonly location: Location,
    private readonly seoService: SeoService,
    private readonly httpClient: HttpClient,
    private readonly transferState: TransferState
  ) {}

  getBannersHome(): Observable<IHomeBanner[]> {
    if (this.transferState.hasKey(PROMO_BANNERS_KEY)) {
      const data = this.transferState.get(PROMO_BANNERS_KEY, null);

      if (data) {
        if (this.isBrowser) this.transferState.remove(PROMO_BANNERS_KEY);
        return of(data);
      }
    }

    let url = `${environment.nmConfigurations}/v1/api/NmBanner/GetBanners`;

    return this.httpClient.get<IHomeBannerResponse<IHomeBanner[]>>(url).pipe(
      map((response) => {
        return response.result
          .filter((banner) => banner.status)
          .sort((a, b) => a.position - b.position)
          .slice(0, 10);
      }),
      tap((result) => {
        if (!this.isBrowser) this.transferState.set<IHomeBanner[]>(PROMO_BANNERS_KEY, result);
      })
    );
  }

  getTabIndexByPath(path: string): TabIndex {
    this.seoService.updateSeoByPath(path);

    const tabMap: Record<string, TabIndex> = {
      [PATH_NAMES.FLIGHTS]: TabIndex.FLIGHTS,
      [PATH_NAMES.HOTELS]: TabIndex.HOTELS,
      [PATH_NAMES.FLIGHT_HOTEL]: TabIndex.FLIGHT_HOTEL,
      [PATH_NAMES.PACKAGES]: TabIndex.PACKAGES,
      [PATH_NAMES.TRAVEL_INSURANCE]: TabIndex.INSURANCE,
    };

    if (path.includes(PATH_NAMES.HOT_SALE)) return TabIndex.HOT_SALE;

    return tabMap[path] || TabIndex.FLIGHTS;
  }

  updateUrlAndSeoByTabIndex(index: TabIndex) {
    const tabConfig: Record<TabIndex, { path: string; seo?: any }> = {
      [TabIndex.FLIGHTS]: { path: PATH_NAMES.FLIGHTS, seo: SEO.flights },
      [TabIndex.HOTELS]: { path: PATH_NAMES.HOTELS, seo: SEO.hotels },
      [TabIndex.FLIGHT_HOTEL]: { path: PATH_NAMES.FLIGHT_HOTEL, seo: SEO.flightAndHotel },
      [TabIndex.PACKAGES]: { path: PATH_NAMES.PACKAGES, seo: SEO.touristPackages },
      [TabIndex.INSURANCE]: { path: PATH_NAMES.TRAVEL_INSURANCE, seo: SEO.travelInsurance },
      [TabIndex.HOT_SALE]: { path: PATH_NAMES.HOT_SALE },
    };

    const { path, seo } = tabConfig[index] || { path: '/', seo: SEO.home };
    if (seo) this.seoService.updatePageMetaTags(seo);

    if (this.isBrowser) this.location.go(path);
  }
}
